import { useLocation } from 'react-router-dom';
import { SidebarNavItem } from './SidebarNavItem';
import { NavSubTree, TREE_ROOT_LOADERS } from './NavTreeItem';
import { usePins, togglePin } from './PinnedPages';
import { APP_MENU_GROUPS } from '@/components/desktop/appMenu';
import { useState, useEffect } from 'react';
import { useSidebar } from '@/lib/context/SidebarContext';
import { useWindowManager } from '@/lib/context/WindowManagerContext';
import { useAuth } from '@/lib/hooks/useAuth';
import { NotificationsPanel } from '@/components/notifications/NotificationsPanel';
import { GlobalSearch } from '@/components/search/GlobalSearch';

// Single-source menu (appMenu.ts) — the drawer renders every group except
// System, which it keeps as its own collapsed "System & tools" bottom section.
const NAV_GROUPS = APP_MENU_GROUPS.filter(g => g.label !== 'System');

const TRACKED_PREFIXES = ['/employees', '/clients', '/crews', '/jobs'];

const SETTINGS_ITEM = { href: '/settings', icon: 'settings', label: 'Settings' };

const UTILITY_ITEMS = [
  { href: '/my-tasks', icon: 'checklist', label: 'My Tasks' },
  { href: '/roadmap',  icon: 'map',       label: 'Roadmap' },
  { href: '/help',     icon: 'help',      label: 'Help Center' },
];

// v2 key: the 2026-07-30 Browse-first reorg renamed every group, so stale
// v1 state (Sales/People/Resources keys) is deliberately abandoned.
function readGroupState(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem('nav_groups_collapsed_v2');
    if (stored) return JSON.parse(stored);
  } catch { /* corrupted or unavailable storage — use defaults */ }
  return { Workspace: true, Finance: true, Tools: true }; // Browse open by default
}

function saveGroupState(state: Record<string, boolean>) {
  localStorage.setItem('nav_groups_collapsed_v2', JSON.stringify(state));
}

function getLastPath(baseHref: string): string {
  try {
    const stored = localStorage.getItem(`lastNav_${baseHref}`);
    if (stored) return stored;
  } catch { /* storage unavailable — fall back to base */ }
  return baseHref;
}

function saveLastPath(pathname: string) {
  for (const prefix of TRACKED_PREFIXES) {
    if (pathname.startsWith(prefix)) {
      localStorage.setItem(`lastNav_${prefix}`, pathname);
      return;
    }
  }
}

function isActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(href + '/');
}

function NavGroups({
  groupState,
  onToggleGroup,
  onClick,
}: {
  groupState: Record<string, boolean>;
  onToggleGroup: (label: string) => void;
  onClick?: () => void;
}) {
  const { pathname } = useLocation();
  const pins = usePins();
  const { openWindow } = useWindowManager();
  // Pop any nav node out as a window (full-screen on phones; the mobile
  // window bar at the bottom handles minimize/restore).
  const popOut = (href: string, label: string, icon = 'folder_open') => {
    onClick?.(); // close the drawer so the window is visible immediately
    openWindow({ route: href, title: label, icon });
  };
  // #113 — entity items expand into a recursive tree (client › jobs › sections)
  const [openTrees, setOpenTrees] = useState<Set<string>>(new Set());
  const toggleTree = (href: string) =>
    setOpenTrees(prev => {
      const n = new Set(prev);
      if (n.has(href)) n.delete(href); else n.add(href);
      return n;
    });

  return (
    <>
      {/* Pinned — same store as the breadcrumb pin bar */}
      {pins.length > 0 && (
        <div className="mb-2">
          <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Pinned</p>
          {pins.map(p => (
            <div key={p.path} className="flex items-center rounded-lg hover:bg-primary/5">
              {/* #182: pins open as windows by default — the click IS the pop-out */}
              <button
                onClick={() => popOut(p.path, p.label, 'push_pin')}
                className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 text-left"
              >
                <span className="material-symbols-outlined text-base text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>push_pin</span>
                <span className="truncate">{p.label}</span>
              </button>
              <button
                onClick={() => togglePin(p)}
                title="Unpin"
                className="w-9 h-9 flex-shrink-0 flex items-center justify-center text-gray-300 hover:text-red-500"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {NAV_GROUPS.map(group => {
        const hasActiveItem = group.items.some(item => isActive(pathname, item.href));
        const groupCollapsed = (groupState[group.label] ?? group.label !== 'Browse') && !hasActiveItem;

        return (
          <div key={group.label}>
            <button
              onClick={() => onToggleGroup(group.label)}
              className="w-full flex items-center justify-between px-3 mt-3 mb-1 group"
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">
                {group.label}
              </span>
              <span
                className={`material-symbols-outlined text-gray-300 group-hover:text-gray-500 transition-all ${groupCollapsed ? '' : 'rotate-180'}`}
                style={{ fontSize: '14px' }}
              >
                expand_more
              </span>
            </button>

            {!groupCollapsed && group.items.map(item => {
              const effectiveHref = TRACKED_PREFIXES.includes(item.href)
                ? getLastPath(item.href)
                : item.href;
              const hasTree = item.href in TREE_ROOT_LOADERS;
              const treeOpen = openTrees.has(item.href);
              const active = isActive(pathname, item.href);
              // Folder-navigator row: label navigates; select_window pops the
              // page out as a window; the big chevron (tree items) expands.
              return (
                <div key={item.href}>
                  <div className={`flex items-stretch rounded-lg overflow-hidden ${
                    active ? 'bg-primary' : treeOpen ? 'bg-primary/5' : ''
                  }`}>
                    <div className="flex-1 min-w-0">
                      {/* #182: menu picks open as windows by default */}
                      <SidebarNavItem
                        href={effectiveHref}
                        icon={item.icon}
                        label={item.label}
                        isActive={active}
                        isCollapsed={false}
                        onClick={e => { e.preventDefault(); popOut(effectiveHref, item.label, item.icon); }}
                      />
                    </div>
                    {hasTree && (
                      <button
                        onClick={e => { e.preventDefault(); e.stopPropagation(); toggleTree(item.href); }}
                        title={treeOpen ? 'Collapse tree' : 'Browse as tree'}
                        aria-label={treeOpen ? `Collapse ${item.label} tree` : `Expand ${item.label} tree`}
                        className={`w-11 flex-shrink-0 flex items-center justify-center border-l ${
                          active
                            ? 'border-white/20 text-white/80'
                            : 'border-gray-100 text-gray-400 hover:text-primary hover:bg-primary/10'
                        }`}
                      >
                        <span className={`material-symbols-outlined text-xl transition-transform ${treeOpen ? 'rotate-90' : ''}`}>
                          chevron_right
                        </span>
                      </button>
                    )}
                  </div>
                  {treeOpen && <NavSubTree rootHref={item.href} searchable onNavigate={onClick} onPopOut={popOut} />}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

export const Sidebar = () => {
  const location = useLocation();
  const { isMobileOpen, closeMobileSidebar } = useSidebar();
  const { user, logout } = useAuth();
  const { openWindow } = useWindowManager();
  // #182: utility links open as windows by default too
  const popOutUtil = (e: React.MouseEvent, href: string, label: string, icon: string) => {
    e.preventDefault();
    closeMobileSidebar();
    openWindow({ route: href, title: label, icon });
  };
  const [searchOpen, setSearchOpen] = useState(false);
  const [groupState, setGroupState] = useState<Record<string, boolean>>(readGroupState);
  const [utilOpen, setUtilOpen] = useState(false); // bottom menu collapsed by default

  useEffect(() => {
    saveLastPath(location.pathname);
  }, [location.pathname]);

  const toggleGroup = (label: string) => {
    setGroupState(prev => {
      const next = { ...prev, [label]: !prev[label] };
      saveGroupState(next);
      return next;
    });
  };

  return (
    <>
      {/* ── Mobile Drawer ── */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
          isMobileOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            isMobileOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeMobileSidebar}
        />
        <aside
          className={`absolute left-0 top-0 h-full w-72 bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ${
            isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Header */}
          <div className="flex h-14 items-center justify-between px-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-xl">construction</span>
              <span className="text-base font-bold text-gray-900">OchrePoint</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSearchOpen(true)}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"
                title="Search"
              >
                <span className="material-symbols-outlined text-xl">search</span>
              </button>
              <NotificationsPanel />
              <button
                onClick={closeMobileSidebar}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="material-symbols-outlined text-gray-500">close</span>
              </button>
            </div>
          </div>

          {/* User */}
          <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 flex items-center gap-3">
            <div className="bg-primary/10 rounded-full size-9 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-primary text-sm">person</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{user?.username || 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"
              title="Logout"
            >
              <span className="material-symbols-outlined text-base">logout</span>
            </button>
          </div>

          {/* Nav — main menu only; detail-page sections live on the page itself
              as collapsible containers, so they're not duplicated here */}
          <div className="flex-1 overflow-y-auto py-3 px-3">
            <NavGroups groupState={groupState} onToggleGroup={toggleGroup} onClick={closeMobileSidebar} />
          </div>

          {/* Utility + Settings — collapsed into one button by default */}
          <div className="px-3 py-2 border-t border-gray-100">
            <button
              onClick={() => setUtilOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              <span className="flex items-center gap-3">
                <span className="material-symbols-outlined text-[20px] text-gray-400">widgets</span>
                System & tools
              </span>
              <span className={`material-symbols-outlined text-gray-300 transition-transform ${utilOpen ? 'rotate-180' : ''}`} style={{ fontSize: '16px' }}>expand_more</span>
            </button>
            {utilOpen && UTILITY_ITEMS.map(item => (
              <SidebarNavItem
                key={item.href}
                href={item.href}
                icon={item.icon}
                label={item.label}
                isActive={isActive(location.pathname, item.href)}
                isCollapsed={false}
                onClick={e => popOutUtil(e, item.href, item.label, item.icon)}
              />
            ))}
            {utilOpen && user?.role === 'owner' && (
              <SidebarNavItem
                href="/dev"
                icon="developer_mode"
                label="Dev Dashboard"
                isActive={isActive(location.pathname, '/dev')}
                isCollapsed={false}
                onClick={e => popOutUtil(e, '/dev', 'Dev Dashboard', 'developer_mode')}
              />
            )}
            {utilOpen && user?.role === 'owner' && (
              <SidebarNavItem
                href="/dev/architecture"
                icon="account_tree"
                label="Architecture"
                isActive={isActive(location.pathname, '/dev/architecture')}
                isCollapsed={false}
                onClick={e => popOutUtil(e, '/dev/architecture', 'Architecture', 'account_tree')}
              />
            )}
            {utilOpen && <SidebarNavItem
              href={SETTINGS_ITEM.href}
              icon={SETTINGS_ITEM.icon}
              label={SETTINGS_ITEM.label}
              isActive={isActive(location.pathname, SETTINGS_ITEM.href)}
              isCollapsed={false}
              onClick={e => popOutUtil(e, SETTINGS_ITEM.href, SETTINGS_ITEM.label, SETTINGS_ITEM.icon)}
            />}
          </div>
        </aside>
      </div>

      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </>
  );
};
